import { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Tabs,
  message,
  Layout,
  Space,
  Dropdown,
} from "antd";
import { EyeOutlined, FileTextOutlined } from "@ant-design/icons";
import { getAgentsForFilter } from "../../api/constants";
import { getFilterTransaction } from "../../api/transactions/transactionRequest";
import { getZones } from "../../api/zone/zoneRequest";
import TransactionSidebar from "../../components/TransactionSidebar";
import { useSelector } from "react-redux";
import TicketModal from "./TicketModal";
import TransactionFilterForm from "./TransactionFilterForm";
import { getPaymentMethodInFrench } from "../../utils/payment";
import PermissionRender from "../../components/PermissionRender";
import { SOURCE_TYPES } from "../../utils/constants";

const { Content } = Layout;

const TypesTransaction = [
  "Achat de ticket",
  "Paiement d'infraction",
  "Paiement d'abonnement",
  "Paiement macaron",
  "Extension de ticket",
  "Transfert interne",
  "Transfert externe",
];

function TransactionPage() {
  const [transactions, setTransactions] = useState({
    format_general: [],
    format_custom: [],
  });
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);

  const [dateRange, setDateRange] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false); // New state for PDF exporting

  const user = useSelector((state) => state.user.user);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchZones();

    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          setPdfExporting(true);
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          setPdfExporting(false);
          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          setPdfExporting(false);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentsForFilter();
      setUsers(data);
    } catch (error) {
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const data = await getZones();
      setZones(Array.isArray(data) ? data : data.data ?? []);
    } catch (error) {
      // non-blocking, zones list stays empty
    }
  };

  const filterTransactions = async () => {
    if (!dateRange || dateRange.length === 0) {
      message.error("Veuillez sélectionner une plage de dates");
      return;
    }
    setLoading(true);
    try {
      const filters = {
        date_from:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        date_to:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        user_id: selectedUser,
        type: selectedType,
        source: selectedSource,
        payment_method: selectedPaymentMethod,
        zone_id: selectedZone,
      };
      const data = await getFilterTransaction(filters);
      setTransactions(data);
    } catch (error) {
      message.error("Échec de la récupération des transactions");
    } finally {
      setLoading(false);
    }
  };

  const showTicketModal = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedTicket(null);
  };

  const generalColumns = [
    { title: "ID", dataIndex: "id", key: "id" },
    { title: "Expéditeur", dataIndex: ["sender", "full_name"], key: "sender" },
    {
      title: "Destinataire",
      dataIndex: ["receiver", "full_name"],
      key: "receiver",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      align: "right",
    },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Contrat", dataIndex: "zone_name", key: "zone_name" },
    {
      title: "Méthode de Paiement",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (text) => getPaymentMethodInFrench(text),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text) => SOURCE_TYPES[text] || text,
    },
    {
      title: "Date",
      dataIndex: "created_at_without_tz",
      key: "created_at_without_tz",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {record.ticket_parking && (
            <Button
              type="link"
              icon={<FileTextOutlined />}
              onClick={() => showTicketModal(record.ticket_parking)}
            />
          )}
          {record.infraction_id && (
            <Button
              type="link"
              onClick={() =>
                window.open(`/infraction/${record.infraction_id}`, "_blank")
              }
              icon={<EyeOutlined />}
            ></Button>
          )}
          {record.subscription_id && (
            <Button
              type="link"
              onClick={() =>
                window.open(
                  `/subscriptions/${record.subscription_id}`,
                  "_blank"
                )
              }
              icon={<EyeOutlined />}
            ></Button>
          )}
        </Space>
      ),
    },
  ];

  const customColumns = [
    {
      title: "ID Transaction",
      dataIndex: "transaction_id",
      key: "transaction_id",
    },
    {
      title: "Rôle de l'Agent",
      dataIndex: "agent_role_name",
      key: "agent_role_name",
    },
    {
      title: "Montant de la Transaction",
      dataIndex: "transaction_amount",
      key: "transaction_amount",
      align: "right",
      render: (text) => (
        <span
          style={{
            color: text?.includes("+")
              ? "green"
              : text?.includes("-")
              ? "red"
              : "black",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Solde de l'Agent",
      dataIndex: "agent_balance",
      key: "agent_balance",
      align: "right",
    },
    {
      title: "Type de Transaction",
      dataIndex: "transaction_type",
      key: "transaction_type",
    },
    {
      title: "Date de la Transaction",
      dataIndex: "transaction_date",
      key: "transaction_date",
    },
  ];

  const handlePdfClick = (operation) => {
    // Create a filename with timestamp
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `transactions_${timestamp}.pdf`;
    const baseUrl = window.location.origin;

    // Prepare only the necessary data to reduce processing overhead
    const formattedData = transactions.format_general.map((it) => ({
      created_at_without_tz: it.created_at_without_tz,
      reference: it.payment?.reference || it.ticket_parking?.reference || "",
      sender_name: it.sender?.full_name || "",
      receiver_name: it.receiver?.full_name || "",
      amount: it.amount,
      type: it.type,
      zone_name: it.zone_name || "",
      payment_method: getPaymentMethodInFrench(it.payment_method),
      source: SOURCE_TYPES[it.source] || it.source,
    }));

    const data = [
      [
        "Transactions",
        ``,
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss")}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss")}`,
      ],
      [],
      [],
      user.full_name,
      [],
      [
        [
          { text: "Date", type: "string", key: "created_at_without_tz" },
          { text: "Référence", type: "string", key: "reference" },
          { text: "Expéditeur", type: "string", key: "sender_name" },
          { text: "Destinataire", type: "string", key: "receiver_name" },
          { text: "Montant", type: "string", key: "amount", align: "right" },
          { text: "Type", type: "string", key: "type" },
          { text: "Contrat", type: "string", key: "zone_name" },
          {
            text: "Méthode de Paiement",
            type: "string",
            key: "payment_method",
          },
          {
            text: "Source",
            type: "string",
            key: "source"
          }
        ],
      ],
      [formattedData], // Send pre-processed data
      baseUrl,
      {
        total: formattedData.reduce((sum, it) => sum + parseFloat(it.amount || 0), 0),
        label: "Total",
      },
    ];

    // Post message to worker
    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const handleExcelClick = async () => {
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `transactions_${timestamp}.xlsx`;
    const baseUrl = window.location.origin;

    // Prepare only the necessary data to reduce processing overhead
    const formattedData = transactions.format_general.map((it) => ({
      created_at_without_tz: it.created_at_without_tz,
      reference: it.payment?.reference || it.ticket_parking?.reference || "",
      sender_name: it.sender?.full_name || "",
      receiver_name: it.receiver?.full_name || "",
      amount: it.amount,
      type: it.type,
      zone_name: it.zone_name || "",
      payment_method: getPaymentMethodInFrench(it.payment_method),
      source: SOURCE_TYPES[it.source] || it.source,
    }));

    const data = [
      [
        "Transactions",
        `Format Général`,
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss")}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss")}`,
      ],
      [],
      [],
      user.full_name,
      [],
      [
        [
          {
            text: "Date",
            type: "string",
            key: "created_at_without_tz",
          },
          {
            text: "Référence",
            type: "string",
            key: "reference",
          },
          {
            text: "Expéditeur",
            type: "string",
            key: "sender_name",
          },
          {
            text: "Destinataire",
            type: "string",
            key: "receiver_name",
          },
          {
            text: "Montant",
            type: "int",
            key: "amount",
          },
          { text: "Type", type: "string", key: "type" },
          { text: "Contrat", type: "string", key: "zone_name" },
          {
            text: "Méthode de Paiement",
            type: "string",
            key: "payment_method",
          },
          {
            text: "Source",
            type: "string",
            key: "source"
          }
        ],
      ],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce((sum, it) => sum + parseFloat(it.amount || 0), 0),
        label: "Total",
      },
    ];

    // Post message to worker
    workerExcelRef.current.postMessage({ data, filename });
  };

  // Move menu items and tab items outside the component for clarity
  const getPdfMenuItems = (handlePdfClick, pdfExporting) => [
    {
      key: "print",
      label: <span className="hover:text-red-500">Imprimer</span>,
      onClick: () => handlePdfClick("download"),
      disabled: pdfExporting,
    },
    {
      key: "open",
      label: <span className="hover:text-red-500">Ouvrir</span>,
      onClick: () => handlePdfClick("open"),
      disabled: pdfExporting,
    },
  ];

  const getTabItems = (
    transactions,
    generalColumns,
    loading,
    handleExcelClick,
    handlePdfClick,
    pdfExporting,
    customColumns
  ) => [
    {
      key: "1",
      label: "Transactions",
      children: (
        <>
          <div style={{ marginBottom: 16 }} className="flex justify-end">
            <Button
              icon={<FileTextOutlined />}
              onClick={async () => await handleExcelClick()}
              className="hover:border-green-700 hover:text-green-500 mr-4"
            >
              Exporter Excel
            </Button>
            <Dropdown
              menu={{ items: getPdfMenuItems(handlePdfClick, pdfExporting) }}
              disabled={pdfExporting}
              className="hover:border-red-700 hover:text-red-500"
            >
              <Button icon={<FileTextOutlined />} disabled={pdfExporting}>
                Export PDF{" "}
                {pdfExporting ? (
                  "..."
                ) : (
                  <span style={{ marginLeft: 5 }}>▼</span>
                )}
              </Button>
            </Dropdown>
          </div>
          <Table
            dataSource={transactions.format_general}
            columns={generalColumns}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],

              showTotal: (total, range) => {
                const totalAmount = transactions.format_general.reduce(
                  (sum, transaction) =>
                    sum + parseFloat(transaction.amount || 0),
                  0
                );
                const formattedTotal = new Intl.NumberFormat("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(totalAmount);

                return (
                  <span>
                    {`${range[0]}-${range[1]} sur ${total} transactions | `}
                    <strong>Total: {formattedTotal} MAD</strong>
                  </span>
                );
              },
            }}
          />
        </>
      ),
    },
    {
      key: "2",
      label: "Details",
      children: (
        <Table
          dataSource={transactions.format_custom}
          columns={customColumns}
          rowKey="index"
          pagination={{ pageSize: 10 }}
          loading={loading}
        />
      ),
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Row gutter={16}>
          <Col xs={24} lg={19}>
            <Card className="mb-6 shadow-sm">
              <TransactionFilterForm
                users={users}
                zones={zones}
                TypesTransaction={TypesTransaction}
                setDateRange={setDateRange}
                setSelectedUser={setSelectedUser}
                setSelectedType={setSelectedType}
                setSelectedSource={setSelectedSource}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                setSelectedZone={setSelectedZone}
                filterTransactions={filterTransactions}
                loading={loading}
              />
            </Card>
            <Card className="shadow-sm">
              <Tabs
                items={getTabItems(
                  transactions,
                  generalColumns,
                  loading,
                  handleExcelClick,
                  handlePdfClick,
                  pdfExporting,
                  customColumns
                )}
              />
            </Card>
          </Col>
          <Col xs={24} lg={5}>
            <PermissionRender
              requiredPermission={["transactions", "balance_sidebar"]}
            >
              <TransactionSidebar currentUser={user} />
            </PermissionRender>
          </Col>
        </Row>

        <TicketModal
          open={isModalVisible}
          onCancel={handleModalCancel}
          ticket={selectedTicket}
        />
      </Content>
    </Layout>
  );
}

export default TransactionPage;
